// Payments worklist (staff + rep). Every installment across orders, bucketed by state
// (overdue / due / awaiting arrival / paid), with an optional date-range filter on either the
// due date or the date payment was received. Staff act on payments from an order's detail page;
// reps see their own book read-only. Click a row to open the order.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader.jsx';
import StoreSwitcher from '../components/shell/StoreSwitcher.jsx';
import { fetchWorklist, worklistCsvUrl } from '../api/payments.js';
import Pager from '../components/Pager.jsx';
import { fmtMoney } from '../lib/matrix.js';
import { useStoreContext } from '../stores/storeContext.js';
import { useAuthStore } from '../stores/authStore.js';
import { installmentTiming, paymentStatusLabel } from '../lib/labels.js';

const BUCKETS = [
  { key: '', label: 'All' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'due', label: 'Due now' },
  { key: 'awaiting_approval', label: 'Awaiting approval' },
  { key: 'awaiting_arrival', label: 'Not yet due' },
  { key: 'paid', label: 'Paid' },
];
const BUCKET_CLS = {
  overdue: 'bg-error-light text-error',
  due: 'bg-accent-light text-text-primary',
  awaiting_approval: 'bg-warning-light text-warning',
  awaiting_arrival: 'bg-surface-raised text-text-secondary',
  paid: 'bg-success-light text-success',
  void: 'bg-surface-raised text-text-muted',
};
const bucketLabel = (b) => BUCKETS.find((x) => x.key === b)?.label || b.replace(/_/g, ' ');

// Same presets as the dashboard, plus an "All dates" default (a worklist shouldn't hide overdue
// items outside a window unless the user asks it to). The server turns them into exact Sydney-time
// dates; custom dates are sent as typed (whole days, inclusive).
const DATE_PRESETS = [
  { key: 'all', label: 'All dates' },
  { key: 'today', label: 'Today' },
  { key: '7d', label: 'Last 7 days' },
  { key: 'month', label: 'This month' },
  { key: '30d', label: 'Last 30 days' },
  { key: 'quarter', label: 'This quarter' },
  { key: 'year', label: 'This year' },
  { key: 'custom', label: 'Custom' },
];
const sydneyDay = (iso) => new Date(iso).toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney', day: 'numeric', month: 'short', year: 'numeric' });
const defaultCustom = () => {
  const day = (ms) => new Date(ms).toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' });
  return { from: day(Date.now() - 29 * 864e5), to: day(Date.now()) };
};

export default function Payments() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);
  const activeStore = useStoreContext((s) => s.activeStore);
  // Links from a client card / the dashboard / a chase email arrive pre-filtered (?companyId=, ?bucket=).
  const [searchParams, setSearchParams] = useSearchParams();
  const companyId = searchParams.get('companyId') || '';
  const [bucket, setBucket] = useState(searchParams.get('bucket') || '');
  const [datePreset, setDatePreset] = useState('all');
  const [dateField, setDateField] = useState('due'); // 'due' | 'received'
  const [custom, setCustom] = useState(defaultCustom);
  const [data, setData] = useState({ items: [], counts: {}, total: 0 });
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 100;
  const [loading, setLoading] = useState(true);

  const customBad = datePreset === 'custom' && (!custom.from || !custom.to || custom.from > custom.to);

  // Same filters for the list, the pager and the CSV export.
  const params = useMemo(() => {
    const p = {};
    if (bucket) p.bucket = bucket;
    if (companyId) p.companyId = companyId;
    if (activeStore) p.store = activeStore; // scoped by the top-bar store toggle
    if (datePreset !== 'all' && !customBad) {
      p.range = datePreset;
      if (datePreset === 'custom') {
        p.from = custom.from;
        p.to = custom.to;
      }
      p.dateField = dateField;
    }
    return p;
  }, [bucket, activeStore, datePreset, custom, customBad, dateField, companyId]);
  useEffect(() => setPage(1), [params]);
  useEffect(() => {
    setLoading(true);
    fetchWorklist({ ...params, page, pageSize: PAGE_SIZE })
      .then(setData)
      .catch(() => setData({ items: [], counts: {}, total: 0 }))
      .finally(() => setLoading(false));
  }, [params, page]);

  const { items, counts } = data;
  const dateActive = datePreset !== 'all';
  const range = dateActive ? data.range : null;

  return (
    <>
      <PageHeader
        title="Payments"
        subtitle={
          role === 'REP'
            ? 'Your clients’ payments — what’s due, overdue and paid. View-only: open an order to copy its payment link.'
            : 'Every payment due across all orders — deposits, balances, overdue and paid.'
        }
        actions={
          <>
            <a href={worklistCsvUrl(params)} className="rounded-md border border-line px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-raised" title="Download every installment matching these filters">
              Export CSV
            </a>
            <StoreSwitcher />
          </>
        }
      />

      <div className="mb-3 flex flex-wrap gap-2">
        {BUCKETS.map((b) => (
          <button
            key={b.key || 'all'}
            onClick={() => setBucket(b.key)}
            className={`rounded-full px-3 py-1 text-sm ${bucket === b.key ? 'bg-accent text-white' : 'bg-surface-raised text-text-secondary hover:text-text-primary'}`}
          >
            {b.label}
            {b.key && counts[b.key] ? ` · ${counts[b.key]}` : ''}
          </button>
        ))}
      </div>

      {companyId && (
        <div className="mb-3 flex items-center gap-2 text-sm">
          <span className="rounded-full bg-accent-light px-3 py-1 text-text-primary">
            Showing one client{items[0]?.company ? `: ${items[0].company}` : ''}
          </span>
          <button onClick={() => setSearchParams({}, { replace: true })} className="text-xs text-text-muted hover:text-text-primary hover:underline">
            Show all clients
          </button>
        </div>
      )}

      {/* Date filter — composes with the bucket + store filters above. */}
      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-line bg-surface p-3 shadow-sm">
        <div className="inline-flex rounded-md border border-line p-0.5 text-sm">
          {[
            ['due', 'Due date'],
            ['received', 'Date received'],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setDateField(k)}
              className={`rounded px-3 py-1 ${dateField === k ? 'bg-accent text-white' : 'text-text-secondary hover:bg-surface-raised'}`}
            >
              {label}
            </button>
          ))}
        </div>
        <span className="mx-1 h-5 w-px bg-line" aria-hidden />
        {DATE_PRESETS.map((p) => (
          <button
            key={p.key}
            onClick={() => setDatePreset(p.key)}
            className={`rounded-full px-3 py-1 text-sm transition ${
              datePreset === p.key ? 'bg-accent text-white' : 'bg-surface-raised text-text-secondary hover:text-text-primary'
            }`}
          >
            {p.label}
          </button>
        ))}
        {datePreset === 'custom' && (
          <span className="flex items-center gap-2">
            <input type="date" value={custom.from} max={custom.to} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} className="rounded-md border border-line bg-surface px-2 py-1 text-sm" />
            <span className="text-text-muted">→</span>
            <input type="date" value={custom.to} min={custom.from} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} className="rounded-md border border-line bg-surface px-2 py-1 text-sm" />
          </span>
        )}
        {dateActive && (
          <span className="basis-full text-xs text-text-muted">
            {customBad ? (
              <span className="text-error">The end date must be on or after the start date.</span>
            ) : range ? (
              <>
                {dateField === 'received' ? 'Paid' : 'Due'} {sydneyDay(range.from)} – {sydneyDay(new Date(new Date(range.to).getTime() - 1).toISOString())} (Sydney time)
              </>
            ) : null}
          </span>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-text-muted">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-line bg-surface p-10 text-center text-sm text-text-muted">
          {dateActive ? 'No payments match this date range.' : bucket === 'overdue' ? 'Nothing overdue — nice.' : 'No payments to show here.'}
        </div>
      ) : (
        <>
        {/* Phones: one card per payment instead of a nine-column table. */}
        <ul className="divide-y divide-line rounded-lg border border-line bg-surface shadow-sm md:hidden">
          {items.map((it) => (
            <li key={it.id}>
              <button onClick={() => navigate(`/orders/${it.orderId}`)} className="w-full px-3 py-3 text-left hover:bg-surface-raised/40">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="min-w-0 truncate text-sm text-text-primary">{it.company || '—'}</span>
                  <span className="shrink-0 text-sm font-semibold tabular-nums text-text-primary">{fmtMoney(it.amount, it.currency)}</span>
                </div>
                <div className="mt-0.5 flex items-center justify-between gap-2">
                  <span className="text-xs text-text-secondary">
                    <span className="font-mono">{it.orderNumber || it.orderId.slice(-8)}</span> · {it.label}
                  </span>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${BUCKET_CLS[it.bucket] || ''}`}>{bucketLabel(it.bucket)}</span>
                </div>
                <div className={`mt-0.5 text-[11px] ${it.bucket === 'overdue' ? 'font-medium text-error' : 'text-text-muted'}`}>
                  {it.paidAt ? `Received ${new Date(it.paidAt).toLocaleDateString()}` : installmentTiming({ ...it, status: it.status === 'PAID' ? 'LINK_SENT' : it.status })}
                </div>
              </button>
            </li>
          ))}
        </ul>
        <div className="hidden overflow-x-auto rounded-lg border border-line bg-surface shadow-sm md:block">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-line bg-surface-raised/60 text-left text-xs text-text-muted">
                <th className="px-4 py-2 font-medium">Order</th>
                <th className="px-4 py-2 font-medium">Client</th>
                <th className="px-4 py-2 font-medium">Payment</th>
                <th className="px-4 py-2 text-right font-medium">Amount</th>
                <th className="px-4 py-2 font-medium">Due</th>
                <th className="px-4 py-2 font-medium">Received</th>
                <th className="px-4 py-2 font-medium">State</th>
                <th className="px-4 py-2 text-right font-medium">Days overdue</th>
                <th className="px-4 py-2 font-medium">Order paid</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr
                  key={it.id}
                  onClick={() => navigate(`/orders/${it.orderId}`)}
                  className="cursor-pointer border-b border-line last:border-0 hover:bg-surface-raised/40"
                >
                  <td className="px-4 py-2 font-mono text-xs text-text-secondary">{it.orderNumber || it.orderId.slice(-8)}</td>
                  <td className="px-4 py-2 text-text-primary">{it.company || '—'}</td>
                  <td className="px-4 py-2 text-text-secondary">{it.label}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-medium text-text-primary">{fmtMoney(it.amount, it.currency)}</td>
                  <td className="px-4 py-2 text-xs text-text-muted">{installmentTiming({ ...it, status: it.status === 'PAID' ? 'LINK_SENT' : it.status })}</td>
                  <td className="px-4 py-2 text-xs text-text-muted">
                    {it.paidAt ? new Date(it.paidAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${BUCKET_CLS[it.bucket] || ''}`}>{bucketLabel(it.bucket)}</span>
                  </td>
                  <td className="px-4 py-2 text-right text-xs tabular-nums">
                    {it.bucket === 'overdue' && it.dueDate ? (
                      <span className="font-medium text-error">{Math.max(1, Math.floor((Date.now() - new Date(it.dueDate).getTime()) / 86400000))}</span>
                    ) : (
                      <span className="text-text-muted">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-text-muted">{paymentStatusLabel(it.orderPaymentStatus)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
      {!loading && <Pager page={page} pageSize={PAGE_SIZE} total={data.total || 0} onPage={setPage} />}
    </>
  );
}
